import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { AppUserProfile, AppUserService } from '../../../core/services';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import {
  combineBangladeshMobileNumber,
  extractBangladeshMobileLocalPart,
  normalizeBangladeshMobileLocalInput,
  BANGLADESH_COUNTRY_CODE
} from '../../../core/utils/bd-mobile.util';

export interface EmployeeSaveEvent {
  mode: 'create' | 'edit';
  refId?: number;
}

@Component({
  selector: 'app-add-edit-employee',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './add-edit-employee.component.html',
  styleUrls: ['./add-edit-employee.component.css', './../../../../CSSFiles/BTN_Style.css']
})
export class AddEditEmployeeComponent implements OnInit {
  readonly mobilePrefix = BANGLADESH_COUNTRY_CODE;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialEmployee: AppUserProfile | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<EmployeeSaveEvent>();
  @Output() editImage = new EventEmitter<void>();

  employeeForm: FormGroup;
  isSubmitting = false;
  error: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly appUsers: AppUserService
  ) {
    this.employeeForm = this.fb.group({
      name: ['', Validators.required],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^1\d{9}$/)]],
      email: ['', Validators.email]
    });
  }

  ngOnInit(): void {
    if (this.mode === 'create') {
      return;
    }

    this.employeeForm.patchValue({
      name: this.initialEmployee?.name ?? '',
      mobileNumber: extractBangladeshMobileLocalPart(this.initialEmployee?.mobileNumber),
      email: this.initialEmployee?.email ?? ''
    });
  }

  closeModal(): void {
    this.close.emit();
  }

  hasError(controlName: string, errorKey?: string): boolean {
    const control = this.employeeForm.get(controlName);
    if (!control || !control.touched) {
      return false;
    }
    if (!errorKey) {
      return control.invalid;
    }
    return control.hasError(errorKey);
  }

  openImageUpdate(): void {
    if (this.mode !== 'edit') {
      return;
    }
    this.editImage.emit();
  }

  normalizeMobileInput(): void {
    const control = this.employeeForm.get('mobileNumber');
    control?.setValue(normalizeBangladeshMobileLocalInput(control.value), { emitEvent: false });
  }

  onSubmit(): void {
    this.normalizeMobileInput();
    if (this.employeeForm.invalid) {
      this.employeeForm.markAllAsTouched();
      this.error = 'Please fix the highlighted fields.';
      return;
    }

    const mobileNumber = combineBangladeshMobileNumber(this.employeeForm.getRawValue()['mobileNumber']);
    if (!mobileNumber) {
      this.error = 'Enter a valid mobile number.';
      return;
    }

    const payload = {
      name: `${this.employeeForm.getRawValue()['name'] ?? ''}`.trim(),
      mobileNumber,
      email: `${this.employeeForm.getRawValue()['email'] ?? ''}`.trim()
    };

    this.isSubmitting = true;
    this.error = null;

    const request$ = this.mode === 'edit'
      ? this.appUsers.updateEmployee({
          id: Number(this.initialEmployee?.id),
          name: payload.name,
          mobileNumber: payload.mobileNumber,
          email: payload.email || undefined
        })
      : this.appUsers.createEmployee({
          name: payload.name,
          mobileNumber: payload.mobileNumber,
          email: payload.email || undefined
        });

    request$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (res: any) => {
          if (`${res?.code ?? ''}` !== '000') {
            this.error = `${res?.message ?? `Failed to ${this.mode === 'edit' ? 'update' : 'create'} subuser.`}`;
            return;
          }

          const refId = Number(res?.refId ?? this.initialEmployee?.id);
          this.save.emit({
            mode: this.mode,
            refId: Number.isFinite(refId) ? refId : undefined
          });
        },
        error: (err: any) => {
          this.error =
            err?.error?.message ||
            err?.message ||
            `Failed to ${this.mode === 'edit' ? 'update' : 'create'} subuser.`;
        }
      });
  }
}
